// src/components/maps/InstallationsMap.js
import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Map as MapIcon,
    Home as HomeIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Directions as DirectionsIcon
} from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { fetchWithAuth } from '../../services/apiService';
import ReactDOMServer from 'react-dom/server';

// Añadir estilos directamente en un tag style sin el atributo jsx
const markerStyles = `
  .mui-marker-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    background: transparent !important;
    border: none !important;
  }
  
  .mui-marker-icon svg {
    transform-origin: center bottom;
    transition: transform 0.3s ease;
  }
  
  .mui-marker-icon:hover svg {
    transform: scale(1.2);
  }

  .popup-directions-button {
    background-color: #1976d2;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
    width: 100%;
    font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    transition: background-color 0.3s;
  }

  .popup-directions-button:hover {
    background-color: #1565c0;
  }

  .popup-directions-button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  .popup-directions-button svg {
    margin-right: 6px;
  }
`;

// Añadir estilos globalmente al montar el componente
const addGlobalStyles = () => {
    if (!document.getElementById('marker-styles')) {
        const styleEl = document.createElement('style');
        styleEl.id = 'marker-styles';
        styleEl.innerHTML = markerStyles;
        document.head.appendChild(styleEl);
    }
};

// Función para crear un icono de marcador MUI personalizado
const createMuiMarkerIcon = (status) => {
    let IconComponent, color;

    // Seleccionar el icono y color según el estado
    switch (status) {
        case 'completed':
            IconComponent = CheckCircleIcon;
            color = '#4CAF50'; // Verde
            break;
        case 'cancelled':
            IconComponent = CancelIcon;
            color = '#F44336'; // Rojo
            break;
        case 'pending':
        default:
            IconComponent = HomeIcon;
            color = '#FF9800'; // Naranja
            break;
    }

    // Renderizar el componente MUI a HTML
    const iconHtml = ReactDOMServer.renderToString(
        <IconComponent
            style={{
                color,
                fontSize: '38px',
                filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.3))'
            }}
        />
    );

    // Crear un DivIcon de Leaflet con el HTML del icono MUI
    return L.divIcon({
        html: iconHtml,
        className: 'mui-marker-icon',
        iconSize: [38, 38],
        iconAnchor: [19, 38],
        popupAnchor: [0, -38]
    });
};

// Función para renderizar un ícono de direcciones para el botón
const renderDirectionsIcon = () => {
    return ReactDOMServer.renderToString(
        <DirectionsIcon
            style={{
                fontSize: '16px',
                verticalAlign: 'middle'
            }}
        />
    );
};

const InstallationsMap = ({ selectedZoneId, onInstallationClick, height = '600px' }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersLayerRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [installations, setInstallations] = useState([]);

    // Añadir estilos globales al montar el componente
    useEffect(() => {
        addGlobalStyles();
    }, []);

    // Inicializar el mapa al montar el componente
    useEffect(() => {
        initializeMap();
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
            }
        };
    }, []);

    // Cargar instalaciones cuando cambia la zona seleccionada
    useEffect(() => {
        if (mapInstanceRef.current) {
            loadInstallations();
        }
    }, [selectedZoneId]);

    // Inicializar el mapa de Leaflet
    const initializeMap = () => {
        if (!mapRef.current) return;

        try {
            const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 22,
                maxNativeZoom: 19
            });

            const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
                attribution: '&copy; Google Maps',
                maxZoom: 22,
                maxNativeZoom: 20
            });

            const esriSatellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
                maxZoom: 22,
                maxNativeZoom: 17
            });

            // Crear instancia del mapa centrada en Sullana, Perú (o tu ubicación deseada)
            const map = L.map(mapRef.current, {
                center: [-4.9, -80.77],
                zoom: 13,
                maxZoom: 22,
                layers: [googleHybrid]
            });

            // Añadir capa de satélite y control de capas
            const baseMaps = {
                "Mapa Estándar": osm,
                "Satelital Google (Híbrido)": googleHybrid,
                "Satélite Esri": esriSatellite
            };

            L.control.layers(baseMaps).addTo(map);

            mapInstanceRef.current = map;
            markersLayerRef.current = L.layerGroup().addTo(map);

            // Añadir controles
            L.control.scale().addTo(map);

            loadInstallations();
        } catch (error) {
            console.error("Error inicializando mapa:", error);
            setError("Error al inicializar el mapa: " + error.message);
        }
    };

    // Cargar instalaciones desde el API con URL correcta
    const loadInstallations = async () => {
        if (!mapInstanceRef.current || !markersLayerRef.current) return;

        setLoading(true);
        setError(null);

        // Limpiar marcadores existentes
        markersLayerRef.current.clearLayers();

        try {
            // URL correcta para tu entorno - asegúrate de usar el puerto adecuado
            let url = '/api/installations/map';

            if (selectedZoneId) {
                url += `?zone=${selectedZoneId}`;
            }

            const response = await fetchWithAuth(url);

            if (!response.ok) {
                throw new Error(`Error al cargar datos del mapa: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            setInstallations(data);

            if (data.length === 0) {
                setError('No hay instalaciones para mostrar en el mapa');
                setLoading(false);
                return;
            }

            // Procesar instalaciones y añadir al mapa
            // Crear un array para almacenar los puntos válidos para el fitBounds
            const validLatLngs = [];

            data.forEach(installation => {
                // Parsear coordenadas (si existen)
                let lat = null, lng = null;

                if (installation.coordinates) {
                    try {
                        const coordStr = installation.coordinates.toString().replace(/[()]/g, '');
                        const parts = coordStr.split(',').map(p => parseFloat(p.trim()));

                        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                            // Dependiendo del formato: (x,y) donde x=lng, y=lat
                            // O (lat,lng)
                            lat = parts[0];
                            lng = parts[1];

                            // Si lat está fuera de rango, asumir que está invertido
                            if (Math.abs(lat) > 90) {
                                [lng, lat] = [lat, lng];
                            }
                        }
                    } catch (e) {
                        console.error('Error al procesar coordenadas:', e);
                    }
                }

                // Si tenemos coordenadas válidas, crear marcador
                if (lat && lng && !isNaN(lat) && !isNaN(lng) &&
                    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {

                    // Obtener icono MUI según estado
                    const icon = createMuiMarkerIcon(installation.status);

                    // Determinar información para botón de direcciones
                    const hasValidAddress = installation.address && installation.address.trim() !== '' && installation.address !== 'N/A';
                    const destination = hasValidAddress
                        ? encodeURIComponent(installation.address)
                        : `${lat},${lng}`;

                    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

                    // Crear el HTML del popup con botón de direcciones
                    const popupContent = `
              <div>
                <h3>${installation.client_name ||
                        (installation.first_name && installation.last_name ?
                            installation.first_name + ' ' + installation.last_name :
                            'Cliente')}</h3>
                <p><strong>Fecha:</strong> ${installation.installation_date ?
                            new Date(installation.installation_date).toLocaleDateString() : 'No especificada'}</p>
                <p><strong>Técnico:</strong> ${installation.technician || 'No asignado'}</p>
                <p><strong>Estado:</strong> ${installation.status === 'pending' ? 'Pendiente' :
                            installation.status === 'completed' ? 'Completada' : 'Cancelada'
                        }</p>
                <p><strong>Dirección:</strong> ${installation.address || 'N/A'}</p>
                
                <button 
                  class="popup-directions-button" 
                  onclick="window.open('${directionsUrl}', '_blank')"
                  ${!hasValidAddress && !lat ? 'disabled' : ''}
                >
                  ${renderDirectionsIcon()} Cómo llegar
                </button>
              </div>
            `;

                    // Crear marcador
                    const marker = L.marker([lat, lng], { icon })
                        .addTo(markersLayerRef.current)
                        .bindPopup(popupContent);

                    // Añadir evento de clic
                    if (onInstallationClick) {
                        marker.on('click', () => onInstallationClick(installation));
                    }

                    // Añadir a la lista de coordenadas válidas para fitBounds
                    validLatLngs.push([lat, lng]);
                }
            });

            // Ajustar vista del mapa para mostrar todos los marcadores
            if (validLatLngs.length > 0) {
                // Usar un método más seguro para ajustar los límites del mapa
                const bounds = L.latLngBounds(validLatLngs);
                mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30] });
            } else {
                // Si no hay marcadores, mantener la vista predeterminada o mostrar una ubicación específica
                mapInstanceRef.current.setView([-4.9, -80.77], 13);
                console.log('No hay marcadores válidos para ajustar la vista del mapa');
            }

        } catch (error) {
            console.error('Error loading installations:', error);
            setError('Error al cargar instalaciones: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 2, height: 'auto' }}>
            <Typography variant="h6" gutterBottom>
                <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Mapa de Instalaciones
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    position: 'relative',
                    height,
                    border: '1px solid #ddd',
                    borderRadius: 1
                }}
            >
                <div
                    ref={mapRef}
                    style={{
                        height: '100%',
                        width: '100%',
                        borderRadius: 4
                    }}
                />

                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 999
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default InstallationsMap;